/**
 * The offer to pick a plan.
 *
 * A navy panel with a chartreuse "NEXT STEP" eyebrow, wedged into a 380px
 * column beside the greeting. "Next step" was the only thing it said that was
 * not true: nothing was blocked, and the panel itself says so two lines later
 * with "No rush, your current access still works."
 *
 * It is an offer. It says what a plan adds, and it does not pretend to be a
 * step in something.
 */

import { CreditCard } from 'lucide-react'
import { ButtonLink } from '@/design-system/primitives'
import '@/design-system/dashboard.css'

interface DashboardSubscriptionNudgeProps {
  variant: 'free_trial' | 'legacy'
  canManageBilling: boolean
  billingPlansPath: string
  onViewPlansClick: () => void
}

export function DashboardSubscriptionNudge({
  variant,
  canManageBilling,
  billingPlansPath,
  onViewPlansClick,
}: DashboardSubscriptionNudgeProps) {
  const headline = canManageBilling
    ? variant === 'legacy'
      ? 'Add subscription features when you need them'
      : 'Choose a plan when you’re ready'
    : 'Ask an admin about plans'

  const body = canManageBilling
    ? variant === 'legacy'
      ? 'Your legacy credits still cover schema generation. A subscription adds AI-readable files, monitoring, and ongoing checks.'
      : 'Subscriptions unlock hosted AI-readable files, Page Monitoring, Bot Tracking, and more room to keep optimizing.'
    : 'Workspace owners and admins can choose a plan for this workspace.'

  return (
    <section className="ds-card" data-testid="dashboard-subscription-nudge">
      <div className="ds-card__body ds-dash__offer">
        <div>
          <h2 className="ds-h4">{headline}</h2>
          <p>{body}</p>
          {canManageBilling && (
            <span className="ds-small">No rush. Your current access still works.</span>
          )}
        </div>
        {canManageBilling ? (
          <ButtonLink to={billingPlansPath} variant="secondary" onClick={onViewPlansClick}>
            <CreditCard aria-hidden="true" />
            View plans
          </ButtonLink>
        ) : (
          <span className="ds-small">Owners and admins choose the plan.</span>
        )}
      </div>
    </section>
  )
}

export default DashboardSubscriptionNudge
